import type { SiteId } from './data/sites';
import type { TigerSharkState } from './types';
import { civilFromEpochDay, dayOfYear, epochDayFromCivil } from './calendar';
import { YEAR_DAYS, dailyDeath, type PopulationDraws } from './population';
import { TIGER_SHARK_YOUNG_SURVIVAL } from './data/sharks/tiger';

/**
 * The tiger sharks of Maui Nui (M102, a population since M197): a resident predator of Maui Nui's insular shelf, its
 * life as it is measured in Hawaii, counted by year class since birth, the young born in the pupping season, every
 * shark dying at its age's rate. Its draws are the caller's, as the monk seals' are: binomial deaths and Poisson pups
 * on its own stream in the game, expected values in the table (`scripts/tigershark.ts`).
 *
 * **Its count is a floor, scaled from the one count tied to an area.** No one has counted the tiger sharks around Maui:
 * "There is also no reliable way of measuring tiger shark abundance around Maui, because individuals move routinely
 * (but unpredictably) between islands and far out into open ocean" (Meyer et al. 2016, p. 3), and the only Hawaiian
 * estimate, 80 to 130 around Oʻahu in 1967 to 1969, "appears to grossly underestimate the abundance of tiger sharks"
 * because "127 tiger sharks were caught around Oahu during that program" (Wetherbee, Lowe and Crow 1994, *Pacific
 * Science* 48: 95, p. 107; its text reserved, its facts cited). M102 held no count for it, the user's word ("Without a
 * count"); M197 models it, the user's word ("model the tiger sharks in maui nui"), on those 127 over Oʻahu's insular
 * shelf, 927 km² to the 200 m isobath, scaled to Maui County's 3,641 km² (Meyer et al. 2016, Table 1, p. 11), as the
 * sharks of the sizes the State's study caught (`countedFromCm`): 499 (DERIVED; `TIGER_COUNT`). A floor by its own
 * source's word: two years' catch on gear that "favored large sharks", "fishing within a limited portion of the full
 * depth range" (Wetherbee et al. 1994, p. 107), the density taken as Oʻahu's where "the extensive Maui Nui insular shelf
 * ... may be able to support a larger number of tiger sharks" (Meyer et al. 2018, p. 11): open. The State's study
 * followed at least 37 large sharks in Maui's waters in 2013 to 2015 (26 tagged there, 6 of Oʻahu's 15 and 5 tagged
 * before, Meyer et al. 2016, pp. 1, 44 and 45), a count of sharks seen, not of the sharks there.
 *
 * **Its one fitted rate the young's survival** (`TIGER_SHARK_YOUNG_SURVIVAL`), set so the population replaces itself:
 * no trend of Hawaii's tiger sharks is measured, none is fished at Maui Nui since the State's law ("Act 51 (House Bill
 * 553) makes it illegal to knowingly capture, entangle, or kill a shark in state marine waters", from 1 January 2022,
 * DLNR's announcement), and the longline fleets' few (NOAA's 2024 bycatch reports, 2 observed and 1 in the open North
 * Pacific) are none of Maui Nui's shelf: open.
 *
 * Meyer et al. 2016 is *Spatial Dynamics of Tiger Sharks Around Maui and Oahu*, the State of Hawaii's study after the
 * Maui bites of 2012 to 2015 (a report to DLNR, Hawaii Institute of Marine Biology). It states no terms and its site
 * reserves all rights; its month by month count, a figure read whole, is not carried (M120), and its facts the game
 * cites (M123). What the card says of the sharks' lives at Maui is from Meyer et al. 2018, the same study's paper,
 * "Habitat geography around Hawaii's oceanic islands influences tiger shark (Galeocerdo cuvier) spatial behaviour and
 * shark bite risk at ocean recreation sites" (*Scientific Reports* 8:4945), "licensed under a Creative Commons
 * Attribution 4.0 International License". Its growth, maturity and litters are from Meyer et al. 2014, "Growth and
 * maximum size of tiger sharks (Galeocerdo cuvier) in Hawaii" (*PLoS ONE* 9:e84799): "The work is made available under
 * the Creative Commons CC0 public domain dedication" (its page at PLOS, read in M120).
 *
 * Its predation on the honu and the monk seals stays in their own survival, measured with the sharks there (open: a
 * shark's take of them is not read).
 */

/** A shark's life, each figure with its source: the tiger shark's (M102) and the whitetip reef shark's (M219, `whitetips.ts`). */
export interface SharkLife {
  id: 'tiger' | 'whitetip';
  /** Growth, von Bertalanffy: its rate a year, the total length the average shark grows toward and its length at birth, cm; and the largest measured, cm. */
  growthPerYear: number;
  asymptoteCm: number;
  birthCm: number;
  largestMeasuredCm: number;
  /** The average ages, years, a female and a male are mature at. */
  femaleMatureYears: number;
  maleMatureYears: number;
  /** Pups a litter on average, the least and the most of the range given, and the years between a female's litters. */
  litterPups: readonly [number, number];
  yearsBetweenLitters: number;
  /** Natural death a year of the sharks from the age females mature at, and the oldest class, years: its sharks die at the year's turn. */
  adultDeathPerYear: number;
  oldestYears: number;
  /** A year's survival of the young, from birth to the age females mature at: the one fitted rate. */
  youngSurvival: number;
  /** The share of the young born female. */
  femaleShare: number;
  /** The pupping season, its first and last days as [month, day]: every pup born in it, evenly. */
  pupping: { from: readonly [number, number]; to: readonly [number, number] };
  /** The least total length the count holds, cm. */
  countedFromCm: number;
}

/** The tiger shark's life, with what its count and its links read. */
export interface TigerSharkLife extends SharkLife {
  id: 'tiger';
  /** The depths of the seafloor its life centers on at Maui, m. */
  usualFloorM: readonly [number, number];
  /** The least and the most of the main islands' shark programs' shares of tiger sharks with food that held sea turtles. */
  turtleStomachShare: readonly [number, number];
  /** The count's floor (above): the sharks Oʻahu's program caught, its shelf and Maui Nui's, km². */
  oahuCaught: number;
  oahuShelfKm2: number;
  mauiNuiShelfKm2: number;
}

/** The tiger shark, *Galeocerdo cuvier* (niuhi, DLNR's shark identification guide), at Maui Nui, each figure with its source as read (M102). */
export const TIGER_SHARK: TigerSharkLife = {
  id: 'tiger',
  /**
   * Meyer et al. 2014, Table 5 (p. 5), the optimal model for both sexes from
   * 37 recaptures across Hawaii: k 0.31, PCL∞ 311.47 cm, "TL∞" 406.14 cm
   * (bootstrap 95% interval 383.23 to 436.24), the length the average shark
   * grows toward; the curve drawn "assuming a birth size of 85 cm TL" (p. 3).
   * Its length at 5 years, 338 cm, is the paper's "tiger sharks in Hawaii
   * reach 340 cm TL by age 5" (p. 6). Larger ones are many: "the largest
   * female was 464 cm TL" (p. 3), and of its 420 sharks those of at least
   * 400 cm were 10% (M102's review: the card had said "406 cm at most").
   */
  growthPerYear: 0.31,
  asymptoteCm: 406.14,
  birthCm: 85,
  largestMeasuredCm: 464,
  /** "on average female tiger sharks reach maturity by age 5 and males by age 4" (Meyer et al. 2014, p. 7, its growth against Whitney and Crow 2007's sizes at maturity). */
  femaleMatureYears: 5,
  maleMatureYears: 4,
  /**
   * "They reproduce on a triennial cycle with average litter sizes of 30 to
   * 50 pups" (Meyer et al. 2014, p. 1, from Whitney and Crow 2007, unread: its
   * publisher's login host is refused here).
   */
  litterPups: [30, 50],
  yearsBetweenLitters: 3,
  /**
   * "the modal bathymetry depth, and likely centre of tiger shark activity,
   * is between 50 and 100 m" (Meyer et al. 2018, p. 2): the seafloor's depth
   * under its satellite fixes, the shark itself "making repeated “yo-yo”
   * dives between the seabed and the surface" (p. 11); and "most
   * Maui-tagged sharks were highly site-attached to the insular shelf
   * surrounding Maui Nui for the 21-month duration of monitoring", while
   * "Oahu-tagged tiger sharks undertook seasonal migrations to Maui" (p. 11),
   * "a distinct winter peak coinciding with tiger shark mating season" (p.
   * 9). The month by month count of Oʻahu's visitors (Meyer et al. 2016's
   * Fig. 22, M102) is no longer carried (M120): the report states no terms,
   * and the paper puts it in a supplement this environment cannot reach.
   */
  usualFloorM: [50, 100],
  /**
   * "Intensive shark research and eradication programs periodically
   * conducted around the main Hawaiian Islands found that 18% (Ikehara 275),
   * 10.8% (Fujimoto and Sakuda 230), and 12.7% (Tester 487) of the tiger
   * sharks examined with food in their stomachs had been feeding on turtles"
   * (Balazs 1980, *Synopsis of biological data on the green turtle in the
   * Hawaiian Islands*, NOAA Technical Memorandum NMFS-SWFC-7, p. 18; its NOAA
   * repository record: "Rights Information: Public Domain"), the honu's link
   * (M120: until then Wass 1971's stomachs by prey, a dissertation whose
   * reproduction is "prohibited without written permission", its shares
   * cited facts by M68's rule; replaced by the clearer terms and the three
   * programs' share). The rest of
   * its diet in words: "Reef-associated organisms form the bulk of tiger
   * shark diet in Hawaii" (Meyer et al. 2018, p. 11).
   */
  turtleStomachShare: [0.108, 0.18],
  /**
   * Hamel and Cope's estimator from growth, "M = 1.55k" (2022, *Fisheries Research* 256: 106477, Eq. 11, p. 6; NOAA
   * authors), on Meyer et al. 2014's k of 0.31: 0.4805 a year (DERIVED), its 95% interval the estimate times and over 5.3
   * ("for M given k, the factor would be 5.3", p. 7). Their estimator from the oldest age, "M = 5.40/Amax" (Eq. 7, p. 3),
   * ranked first, needs an age seen: no Hawaiian tiger shark has been aged, and they advise against "attempting to
   * extrapolate maximum age, or to use a proxy" (p. 6), as Natanson et al.'s 7 ln 2 / k is (1999, *Fishery Bulletin*
   * 97: 944, p. 945, a US government work; 0.345 a year on Hawaii's k, review M197's: first taken here, its interval
   * given as the oldest age's 1.8); the Atlantic's "Maximum age was estimated to be 27.3 years" (p. 946), from slower
   * growth (k 0.178), would give 0.198 (open). From female maturity on; before it, the fitted young's.
   */
  adultDeathPerYear: 1.55 * 0.31,
  /**
   * The oldest class, years: 15, its sharks dead at the year's turn, Natanson et al.'s longevity on Hawaii's k (7 ln 2 /
   * 0.31, 15.65 years; a cut only: at the adults' rate 0.8% of a five-year-old's year class lives to 15).
   */
  oldestYears: 15,
  youngSurvival: TIGER_SHARK_YOUNG_SURVIVAL,
  /** "in utero sex ratios in Hawaii are close to 1:1" (Meyer et al. 2014, p. 7, from Whitney and Crow 2007). */
  femaleShare: 0.5,
  /**
   * "Pups are then born 15-16 months later, peaking in September and October of the following year" (DLNR's *Mating
   * and Reproduction* page, its State's rights reserved, its fact cited): the two months of the peak taken as the
   * season, the births outside them not read (open).
   */
  pupping: { from: [9, 1], to: [10, 31] },
  /** Wetherbee et al. 1994, p. 107, and Meyer et al. 2016, Table 1 (above); Meyer et al. 2018's sharks "217 to 448 cm Total Length" (p. 2), caught at Maui and Oʻahu. */
  oahuCaught: 127,
  oahuShelfKm2: 927,
  mauiNuiShelfKm2: 3_641,
  countedFromCm: 217,
};

/** Its total length at an age, cm: von Bertalanffy's from its length at birth. */
export const lengthAtAge = (life: SharkLife, ageYears: number): number =>
  life.asymptoteCm - (life.asymptoteCm - life.birthCm) * Math.exp(-life.growthPerYear * ageYears);

/**
 * The tiger sharks' share of their time off the shelf, over the open ocean (M211, the user's word: "show the tiger shark
 * in blue water when its appropriate"): "the majority (78.7%) of satellite location estimates occurred in coastal (<500
 * m depth) waters around Oahu, Maui and Hawaii Island", seven of the sharks making "extensive (>100 km up to 1,460 km)
 * offshore movements ... with some individuals spending months in open-ocean" (Meyer et al. 2018, *Scientific Reports*
 * 8: 4945, CC BY 4.0, p. 2; the sharks tagged at Maui and at Oʻahu together, MEASURED): 0.213 over water deeper than 500
 * m, a fix's share taken as a day's (a reading: the fixes came more often near Maui's land receivers, p. 13, so the
 * open ocean's share of the time is likely higher, not measured). The 78.7% kept on the shelf's habitats, by area, the
 * 200 to 500 m band's few fixes with them (a reading). Its sharks "217 to 448 cm Total Length" (p. 2), so the share is
 * the sharks' of those sizes (`offShelfSized`); the smaller, none of them tracked, kept on the shelf (a reading, open).
 */
export const TIGER_SHARK_OFF_SHELF = 1 - 0.787;

/**
 * A tagged tiger shark's trip off the shelf, days (M211, JUDGED): "some sharks in Hawaii ... also make extensive and
 * repeated offshore looping excursions lasting several weeks" (Holland et al. 2019, *Frontiers in Marine Science* 6: 37,
 * CC BY, p. 3, from Meyer et al. 2018), three weeks the least "several" reads; no trip's length measured.
 */
export const TIGER_SHARK_TRIP_DAYS = 21;

/** Whether a site has the tiger shark as read: Maui Nui, where the State's study tagged and followed them. */
export const siteHasTigerSharks = (siteId: SiteId): boolean => siteId === 'maui_nui';

/** Maui Nui's sharks of the counted sizes (above): Oʻahu's catch over its shelf, times Maui Nui's shelf. */
export const TIGER_COUNT = (TIGER_SHARK.oahuCaught / TIGER_SHARK.oahuShelfKm2) * TIGER_SHARK.mauiNuiShelfKm2;

/** The classes: 0 to `oldestYears`. */
export const sharkClasses = (life: SharkLife): number => life.oldestYears + 1;

/**
 * Pups a year to each female of a year class: none before maturity, then a litter of the range's middle every
 * `yearsBetweenLitters` (by her class, so she pups through the whole season the year she turns mature, as the monk
 * seals' are counted: by her age on the day she would pup only through its second half).
 */
export const pupsAt = (life: SharkLife, cls: number): number =>
  cls < life.femaleMatureYears ? 0 : (life.litterPups[0] + life.litterPups[1]) / 2 / life.yearsBetweenLitters;

const COMMON_YEAR_FIRST = epochDayFromCivil(2001, 1, 1);
const seasonDays = (life: SharkLife): { first: number; last: number } => ({
  first: epochDayFromCivil(2001, life.pupping.from[0], life.pupping.from[1]) - COMMON_YEAR_FIRST + 1,
  last: epochDayFromCivil(2001, life.pupping.to[0], life.pupping.to[1]) - COMMON_YEAR_FIRST + 1,
});

/** The day of the year pups are born on, on average (1 the first of January): the season's middle, in a common year. */
function birthDay(life: SharkLife): number {
  const { first, last } = seasonDays(life);
  return (first + last) / 2;
}

/** The share of a year's births born on a day: an even share of the season's days if the day is in it, else none. */
function birthShare(life: SharkLife, epochDay: number): number {
  const { month, day } = civilFromEpochDay(epochDay);
  const [fm, fd] = life.pupping.from;
  const [tm, td] = life.pupping.to;
  if (!((month > fm || (month === fm && day >= fd)) && (month < tm || (month === tm && day <= td)))) return 0;
  const { first, last } = seasonDays(life);
  return 1 / (last - first + 1);
}

/** A shark's chance of dying in a day at its age: the young's survival a year before female maturity, the adults' death after. */
export const sharkDailyDeath = (life: SharkLife, ageYears: number): number =>
  ageYears < life.femaleMatureYears ? dailyDeath(life.youngSurvival) : 1 - Math.exp(-life.adultDeathPerYear / YEAR_DAYS);

/** A class's sharks' mean age, years, on a day: its years and the share of a year since the pups' mean birth day (class 0 this year's pups). */
export const sharkAgeYears = (life: SharkLife, cls: number, epochDay: number): number => cls + (dayOfYear(epochDay) - birthDay(life)) / YEAR_DAYS;

/** Whether a class's sharks are of the sizes tracked off the shelf (M211's review: `countedFromCm`, Meyer et al. 2018's least) on a day. */
export const offShelfSized = (life: SharkLife, cls: number, epochDay: number): boolean => lengthAtAge(life, Math.max(0, sharkAgeYears(life, cls, epochDay))) >= life.countedFromCm;

/**
 * A day of the sharks' lives: on the first of January every class a year older, the oldest's sharks dead of age; in the
 * season the day's pups, each female of each class at its class's rate a year (`pupsAt`), the day's share of the year's
 * births; then every class's deaths at its mean age's rate.
 */
export function stepTigerSharks(life: SharkLife, s: TigerSharkState, epochDay: number, draws: PopulationDraws): void {
  const c = s.counts;
  if (dayOfYear(epochDay) === 1) {
    for (let k = c.length - 1; k > 0; k--) c[k] = c[k - 1] as number;
    c[0] = 0;
  }
  const share = birthShare(life, epochDay);
  if (share > 0) {
    let pups = 0;
    for (let k = 0; k < c.length; k++) pups += (c[k] as number) * life.femaleShare * pupsAt(life, k);
    c[0] = (c[0] as number) + draws.count(pups * share);
  }
  for (let k = 0; k < c.length; k++) {
    const n = c[k] as number;
    if (n > 0) c[k] = n - draws.share(n, sharkDailyDeath(life, sharkAgeYears(life, k, epochDay)));
  }
}

/**
 * One tagged shark's day (M203), as its class's (`stepTigerSharks`' rates): a year older on the first of January, dead of
 * its age past the oldest class as its class is, then dead by its class's mean age's chance or not. `u` its own even draw.
 */
export function stepOneShark(life: SharkLife, shark: { cls: number }, epochDay: number, u: () => number): 'old age' | 'natural' | null {
  if (dayOfYear(epochDay) === 1) {
    if (shark.cls >= sharkClasses(life) - 1) return 'old age';
    shark.cls += 1;
  }
  return u() < sharkDailyDeath(life, sharkAgeYears(life, shark.cls, epochDay)) ? 'natural' : null;
}

/** What the drawing and the card read on a day: Maui Nui's sharks, this year's pups among them, those of the counted sizes (`countedFromCm`) and the adults (from female maturity). */
export interface SharkCounts {
  population: number;
  pups: number;
  counted: number;
  adults: number;
}
export function sharkCounts(life: SharkLife, s: TigerSharkState, epochDay: number): SharkCounts {
  const out: SharkCounts = { population: 0, pups: s.counts[0] as number, counted: 0, adults: 0 };
  s.counts.forEach((n, k) => {
    const age = sharkAgeYears(life, k, epochDay);
    out.population += n;
    if (lengthAtAge(life, Math.max(0, age)) >= life.countedFromCm) out.counted += n;
    if (age >= life.femaleMatureYears) out.adults += n;
  });
  return out;
}

export function emptySharks(life: SharkLife): TigerSharkState {
  return { counts: Array<number>(sharkClasses(life)).fill(0) };
}
